import { createRecipeContext, type HTMLChakraProps, type UnstyledProp } from '@chakra-ui/react';
import type { LucideIcon } from 'lucide-react';
import * as React from 'react';

import {
  statusBadgeConfigMap,
  statusBadgeIconOpacity,
  statusBadgeIconSize,
  type StatusBadgeColorPalette,
  type StatusBadgeSize,
  type StatusBadgeStatus,
} from './status-badge.kds';

export interface StatusBadgeProps extends Omit<HTMLChakraProps<'span'>, 'colorPalette'>, UnstyledProp {
  /** Known status — auto-resolves colorPalette, label, and icon. */
  status?: StatusBadgeStatus;
  /** Show the mapped icon for the status (default false). Ignored when `icon` is explicitly passed. */
  showIcon?: boolean;
  /** Color scheme (auto-resolved from status, or manual). */
  colorPalette?: StatusBadgeColorPalette;
  /** Badge size. Defaults to `md`. */
  size?: StatusBadgeSize;
  /** Lucide icon override (or manual icon in custom mode). When provided, renders the withIcon variant. */
  icon?: LucideIcon;
  /** Label text (auto-resolved from status, or manual). */
  children?: React.ReactNode;
}

const { withContext } = createRecipeContext({ key: 'statusBadge' });
const StatusBadgeRoot = withContext('span') as React.ForwardRefExoticComponent<
  HTMLChakraProps<'span'> & UnstyledProp & { variant?: string; colorPalette?: string; size?: string } & React.RefAttributes<HTMLSpanElement>
>;

export const StatusBadge = React.forwardRef<HTMLSpanElement, StatusBadgeProps>(function StatusBadge(props, ref) {
  const { status, showIcon = false, icon, colorPalette, size, children, ...rest } = props;

  const config = status ? statusBadgeConfigMap[status] : undefined;

  const resolvedColorPalette = colorPalette ?? config?.colorPalette ?? 'neutral';
  const resolvedIcon: LucideIcon | undefined = icon ?? (showIcon && config ? config.icon : undefined);
  const resolvedChildren = children ?? config?.label;

  const Icon = resolvedIcon;
  const variant = Icon ? 'withIcon' : 'default';

  return (
    <StatusBadgeRoot ref={ref} variant={variant} colorPalette={resolvedColorPalette} size={size} {...rest}>
      {Icon && (
        <Icon
          data-testid="status-badge-icon"
          size={statusBadgeIconSize}
          style={{ opacity: statusBadgeIconOpacity, flexShrink: 0 }}
        />
      )}
      {resolvedChildren}
    </StatusBadgeRoot>
  );
});
StatusBadge.displayName = 'StatusBadge';
